import Select from "../../components/ui/Select";
import DateRangeFilter from "../../components/ui/DateRangeFilter";
import Pagination from "../../components/ui/Pagination";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./FXPage.css";

// Import all necessary components and services
import SetFxPricingModal from "../../compoonents/SetFxPricingModal/SetFxPricingModal";
import AddFxRateModal from "../../compoonents/AddFxRateModal/AddFxRateModal";
import { MessageType } from "../../types/exchange-rate";
import { getFxTransactions } from "../../services/fxService";
import ComingSoonAction from "../../compoonents/ComingSoonAction/ComingSoonAction";
import { FxRate, FxTransaction } from "../../types/fx";

// Icon Imports
import { FiEdit2 } from "react-icons/fi";
import { IoEyeOutline, IoSearchOutline } from "react-icons/io5";
import { PiPaperPlaneTiltBold } from "react-icons/pi";
import { GoDownload } from "react-icons/go";
import { getLatestExchangeRate } from "../../services/exchangeRate";
import { toast } from "react-toastify";

// Types
type FxTab = "pricing" | "transaction";
interface CurrencyPair {
  from: string;
  to: string;
  label: string;
  rate: number | null;
  change: number | null;
  lastUpdated: string | null;
  isActive: boolean;
}

const DEFAULT_CURRENCY_PAIRS: CurrencyPair[] = [
  {
    from: "NGN",
    to: "GBP",
    label: "NGN → GBP",
    rate: null,
    change: null,
    lastUpdated: null,
    isActive: true,
  },
  {
    from: "NGN",
    to: "USD",
    label: "NGN → USD",
    rate: 755.5,
    change: -1.2,
    lastUpdated: null,
    isActive: false,
  },
  {
    from: "NGN",
    to: "EUR",
    label: "NGN → EUR",
    rate: 825.75,
    change: 0.8,
    lastUpdated: null,
    isActive: false,
  },
  {
    from: "USD",
    to: "GBP",
    label: "USD → GBP",
    rate: 0.79,
    change: 1.5,
    lastUpdated: null,
    isActive: false,
  },
];

const sortRatesByDate = (rates: RateHistory[]) =>
  [...rates].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
  );

interface RateHistory {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  buyRate: string;
  sellRate: string;
  createdAt: string;
  updatedAt: string;
  adminId: string | null;
}

// --- Main Page Component ---
const FXPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FxTab>("pricing");

  return (
    <div className="fx-page">
      <div className="page-content-wrapper">
        <div className="fx-tabs">
          <button
            className={activeTab === "pricing" ? "active" : ""}
            onClick={() => setActiveTab("pricing")}
          >
            FX Pricing
          </button>
          <button
            className={activeTab === "transaction" ? "active" : ""}
            onClick={() => setActiveTab("transaction")}
          >
            FX Transaction
          </button>
        </div>
        <div className="fx-content">
          {activeTab === "pricing" && <FxPricingTab />}
          {activeTab === "transaction" && <FxTransactionTab />}
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT for FX Pricing Tab ---
// (Kept exactly as it was, no changes needed here for the Date Picker task)
const FxPricingTab: React.FC = () => {
  const [fxPricings, setFxPricings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fromCurrency, setFromCurrency] = useState("NGN");
  const [toCurrency, setToCurrency] = useState("GBP");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingRate, setEditingRate] = useState<FxRate | null>(null);
  const [selectedPair, setSelectedPair] = useState<CurrencyPair>(
    DEFAULT_CURRENCY_PAIRS[0],
  );
  const [message, setMessage] = useState<MessageType | null>(null);
  const [allPairs, setAllPairs] = useState<CurrencyPair[]>(
    DEFAULT_CURRENCY_PAIRS,
  );
  const [history, setHistory] = useState<RateHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const showMessage = useCallback(
    (text: string, type: MessageType["type"] = "info") => {
      setMessage({ text, type });
      setTimeout(() => setMessage(null), 5000);
    },
    [],
  );

  const fetchRateForPair = useCallback(
    async (pair: CurrencyPair, rates: RateHistory[]) => {
      if (!pair.isActive) return pair;

      try {
        const sorted = sortRatesByDate(rates);
        if (sorted.length > 0) {
          const latestRate = sorted[0];
          const buyRate = Number(latestRate.buyRate);
          const previousRate = sorted[1] ? Number(sorted[1].buyRate) : buyRate;
          const change =
            previousRate > 0
              ? ((buyRate - previousRate) / previousRate) * 100
              : 0;

          return {
            ...pair,
            rate: buyRate,
            change,
            lastUpdated: latestRate.updatedAt,
          };
        }
        return pair;
      } catch (error) {
        console.error(`Error fetching rate for ${pair.label}:`, error);
        return pair;
      }
    },
    [],
  );

  const fetchRateHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getLatestExchangeRate();
      if (response && Array.isArray(response)) {
        const sorted = sortRatesByDate(response);
        setFxPricings(sorted);
        setHistory(sorted);
        return sorted;
      }
      setFxPricings([]);
      setHistory([]);
      return [] as RateHistory[];
    } catch (err) {
      setError("Unable to load FX rates. Please try again.");
      setFxPricings([]);
      setHistory([]);
      return [] as RateHistory[];
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPricingData = useCallback(async () => {
    try {
      setIsLoading(true);
      const rates = await fetchRateHistory();
      const updatedPairs = await Promise.all(
        DEFAULT_CURRENCY_PAIRS.map((pair) => fetchRateForPair(pair, rates)),
      );
      setAllPairs(updatedPairs);
      setSelectedPair((prev) => {
        const match = updatedPairs.find(
          (p) => p.from === prev.from && p.to === prev.to,
        );
        return match ?? prev;
      });
    } catch (error) {
      console.error("Error fetching initial data:", error);
      toast.error("Failed to load FX pricing data");
    } finally {
      setIsLoading(false);
    }
  }, [fetchRateHistory, fetchRateForPair]);

  useEffect(() => {
    loadPricingData();
  }, [loadPricingData]);

  const handlePairSelection = (pair: CurrencyPair) => {
    if (!pair.isActive) {
      showMessage(`${pair.label} is not available yet. Coming soon!`, "info");
      return;
    }
    setSelectedPair(pair);
  };

  const handleOpenEditModal = (rate: FxRate) => {
    setEditingRate(rate);
    setIsEditModalOpen(true);
  };

  const handleCloseModals = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditingRate(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModals();
    loadPricingData();
  };

  const sortedHistory = useMemo(() => sortRatesByDate(history), [history]);

  const rateChange = useMemo(() => {
    const validRates = sortedHistory.filter((rate) => Number(rate.buyRate) > 0);
    if (validRates.length < 2) return 0;
    const newest = Number(validRates[0].buyRate);
    const previous = Number(validRates[1].buyRate);
    return previous > 0 ? ((newest - previous) / previous) * 100 : 0;
  }, [sortedHistory]);

  const averageRate = useMemo(() => {
    const validRates = sortedHistory.filter(
      (rate) => Number(rate.buyRate) > 0 && Number(rate.sellRate) > 0,
    );
    if (validRates.length === 0) return 0;
    const averages = validRates.map(
      (rate) => (Number(rate.buyRate) + Number(rate.sellRate)) / 2,
    );
    return averages.reduce((a, b) => a + b, 0) / averages.length;
  }, [sortedHistory]);

  if (loading || isLoading)
    return <div className="page-loading">Loading FX rates…</div>;

  return (
    <div className="fx-pricing-list">
      <div className="pair-selector-section">
        <h3 className="selector-title">Select Currency Pair</h3>
        <div className="pair-selector">
          {allPairs.map((pair) => (
            <button
              key={`${pair.from}-${pair.to}`}
              className={`pair-btn ${selectedPair.from === pair.from && selectedPair.to === pair.to ? "active" : ""} ${!pair.isActive ? "inactive" : ""}`}
              onClick={() => handlePairSelection(pair)}
              disabled={!pair.isActive}
            >
              <div className="pair-label">
                {pair.label}{" "}
                {!pair.isActive && (
                  <span className="coming-soon">Coming Soon</span>
                )}
              </div>
              <div className="pair-rate">
                {pair.rate
                  ? pair.from === "USD"
                    ? `$${pair.rate}`
                    : `₦${pair.rate.toLocaleString()}`
                  : "No data"}
              </div>
            </button>
          ))}
        </div>
      </div>

      {message && (
        <div className={`message message-${message.type}`}>{message.text}</div>
      )}

      <div className="fx-stats-grid">
        <div className="fx-stat-card">
          <div className="fx-stat-card__icon fx-stat-card__icon--orange">
            📈
          </div>
          <div className="fx-stat-card__body">
            <span className="fx-stat-card__label">Average Rate</span>
            <span className="fx-stat-card__value">
              ₦
              {averageRate.toLocaleString(undefined, {
                maximumFractionDigits: 2,
              })}
            </span>
            {sortedHistory.length >= 2 && (
              <span
                className={`fx-stat-card__change ${rateChange >= 0 ? "fx-stat-card__change--up" : "fx-stat-card__change--down"}`}
              >
                {rateChange >= 0 ? "▲" : "▼"} {Math.abs(rateChange).toFixed(2)}%
                vs previous
              </span>
            )}
          </div>
        </div>
        <div className="fx-stat-card">
          <div className="fx-stat-card__icon fx-stat-card__icon--blue">💱</div>
          <div className="fx-stat-card__body">
            <span className="fx-stat-card__label">Active Pair</span>
            <span className="fx-stat-card__value">NGN → GBP</span>
            <span className="fx-stat-card__meta">
              1 of {allPairs.length} pairs live
            </span>
          </div>
        </div>
        <div className="fx-stat-card">
          <div className="fx-stat-card__icon fx-stat-card__icon--teal">
            <span className="fx-stat-card__pair-badge">
              {selectedPair.from}
            </span>
            <span className="fx-stat-card__pair-arrow">→</span>
            <span className="fx-stat-card__pair-badge">{selectedPair.to}</span>
          </div>
          <div className="fx-stat-card__body">
            <span className="fx-stat-card__label">Rate History Entries</span>
            <span className="fx-stat-card__value">{sortedHistory.length}</span>
            <span className="fx-stat-card__meta">
              Last updated:{" "}
              {sortedHistory[0]?.updatedAt
                ? new Date(sortedHistory[0].updatedAt).toLocaleString()
                : "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className="list-header">
        <h3>FX Pricing History</h3>
        <div className="fx-filters">
          <div className="form-group">
            <label>From</label>
            <Select
              value={fromCurrency}
              onChange={(e) => setFromCurrency(e.target.value)}
            >
              <option>NGN</option>
            </Select>
          </div>
          <div className="form-group">
            <label>To</label>
            <Select
              value={toCurrency}
              onChange={(e) => setToCurrency(e.target.value)}
            >
              <option>GBP</option>
            </Select>
          </div>
          <button
            className="action-button primary"
            onClick={() => setIsAddModalOpen(true)}
          >
            + Add New Rate
          </button>
        </div>
      </div>
      {error && (
        <div className="fx-inline-error" role="alert">
          {error}
        </div>
      )}
      {!error && fxPricings.length === 0 && (
        <div className="fx-empty-state">
          No FX rates set. Add a new rate to get started.
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Currency Pair</th>
              <th>Buy Rate</th>
              <th>Sell Rate</th>
              <th>Last Updated</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {fxPricings.length === 0 ? (
              <tr>
                <td colSpan={5} className="fx-table-empty">
                  No rate history yet. Click <strong>+ Add New Rate</strong> to
                  create the first entry.
                </td>
              </tr>
            ) : (
              fxPricings.map((p) => (
                <tr key={p.id}>
                  <td data-label="Currency Pair">
                    <span className="fx-pair-chip">
                      {p.fromCurrency} / {p.toCurrency}
                    </span>
                  </td>
                  <td data-label="Buy Rate">
                    ₦{Number(p.buyRate).toLocaleString()}
                  </td>
                  <td data-label="Sell Rate">
                    ₦{Number(p.sellRate).toLocaleString()}
                  </td>
                  <td data-label="Last Updated">
                    {new Date(p.updatedAt).toLocaleString()}
                  </td>
                  <td data-label="Action" className="action-cell">
                    <div className="table-action-buttons">
                      <button
                        type="button"
                        className="table-action-btn"
                        title="Edit rate"
                        aria-label="Edit rate"
                        onClick={() => handleOpenEditModal(p)}
                      >
                        <FiEdit2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {isEditModalOpen && editingRate && (
        <SetFxPricingModal
          onClose={handleCloseModals}
          onSuccess={handleSaveSuccess}
          editingRate={editingRate}
        />
      )}
      {isAddModalOpen && (
        <AddFxRateModal
          onClose={handleCloseModals}
          onSuccess={handleSaveSuccess}
        />
      )}
    </div>
  );
};

// --- SUB-COMPONENT for FX Transactions Tab ---
// === UPDATED FOR DATE FILTERING ===
// --- SUB-COMPONENT for FX Transactions Tab ---
const FxTransactionTab: React.FC = () => {
  const [transactions, setTransactions] = useState<FxTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtering States
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageLimit = 10;

  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getFxTransactions(
          currentPage,
          pageLimit,
          startDate,
          endDate,
          searchTerm,
        );
        setTransactions(response.results || []);
        setTotalPages(response.pagination?.totalPages ?? 1);
        setTotalItems(response.pagination?.totalItems ?? 0);
      } catch (err) {
        setError("Failed to load FX transactions.");
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(() => {
      fetchTransactions();
    }, 500);
    return () => clearTimeout(timer);
  }, [startDate, endDate, searchTerm, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate, searchTerm]);

  // Client-Side Filtering
  const filteredTransactions = useMemo(() => {
    return transactions.filter((t) => {
      // 1. Filter by Date
      const txnDate = new Date(t.createdAt).setHours(0, 0, 0, 0);

      let dateMatch = true;
      if (startDate) {
        const start = new Date(startDate).setHours(0, 0, 0, 0);
        if (txnDate < start) dateMatch = false;
      }
      if (endDate) {
        const end = new Date(endDate).setHours(0, 0, 0, 0);
        if (txnDate > end) dateMatch = false;
      }

      // 2. Filter by Search
      const searchLower = searchTerm.toLowerCase();
      // Safe access to user properties
      const firstName = t.user?.firstName || "";
      const lastName = t.user?.lastName || "";
      const ref = t.transaction?.transactionReference || "";
      const userId = t.userId || "";

      const fullName = `${firstName} ${lastName}`.toLowerCase();

      const searchMatch =
        fullName.includes(searchLower) ||
        ref.toLowerCase().includes(searchLower) ||
        userId.toLowerCase().includes(searchLower);

      return dateMatch && searchMatch;
    });
  }, [transactions, startDate, endDate, searchTerm]);

  const handleExportCSV = () => {
    const dataToExport = filteredTransactions;
    const headers = [
      "Name",
      "Customer ID",
      "Transaction Ref",
      "FX Type",
      "Rate",
      "Amount",
      "Status",
    ];

    const rows = dataToExport.map((t) => {
      const user = t.user || {};
      const txn = t.transaction || {};
      return [
        `"${user.firstName || ""} ${user.lastName || ""}"`,
        t.userId || "N/A",
        txn.transactionReference || "N/A",
        `${t.fromCurrency} -> ${t.toCurrency}`,
        t.currentRate,
        `${txn.currency || ""} ${txn.amount ?? txn.amountPaid ?? 0}`,
        txn.paymentStatus || "N/A",
      ].join(",");
    });

    const csvContent =
      "data:text/csv;charset=utf-8," + [headers.join(","), ...rows].join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "fx-transactions.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading)
    return <div className="page-loading">Loading transactions...</div>;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="fx-transaction-list">
      {(transactions.length > 0 || searchTerm || startDate || endDate) && (
      <header className="transaction-header">
        <DateRangeFilter startDate={startDate} endDate={endDate} onChange={({ startDate, endDate }) => { setStartDate(startDate); setEndDate(endDate); setCurrentPage(1); }} />
        <div className="right-controls">
          <div className="export-buttons">
            <ComingSoonAction title="FX PDF export — coming soon">
              <button className="export-btn pdf" type="button" disabled>
                PDF <GoDownload />
              </button>
            </ComingSoonAction>
            <button className="export-btn csv" disabled={filteredTransactions.length === 0} onClick={handleExportCSV}>
              CSV <GoDownload />
            </button>
          </div>
          <div className="transaction-search-bar">
            <IoSearchOutline />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </header>) }

      {filteredTransactions.length === 0 ? (
        <div className="fx-empty-state">
          <div className="icon-wrapper">
            <PiPaperPlaneTiltBold />
          </div>
          <h3>
            {searchTerm || startDate || endDate
              ? "No transactions match your filters"
              : "No FX Transactions have been made yet"}
          </h3>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Customer ID</th>
                  <th>Transaction ID</th>
                  <th>FX Type</th>
                  <th>Rate</th>
                  <th>Time Stamp</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((t) => {
                  // --- SAFETY CHECKS START HERE ---
                  const user = t.user || {};
                  const txn = t.transaction || {};

                  const firstName = user.firstName || "";
                  const lastName = user.lastName || "";
                  const email = user.email || "N/A";
                  const fullName = `${firstName} ${lastName}`.trim() || email;

                  // Handle potential missing User ID
                  const userId = t.userId || "N/A";
                  const displayUserId =
                    userId.length > 8 ? `${userId.substring(0, 8)}...` : userId;

                  const currency = txn.currency || "";
                  const amountVal = parseFloat(
                    String(txn.amount ?? txn.amountPaid ?? 0),
                  );
                  const amount = `${currency} ${amountVal.toLocaleString()}`;

                  const status = (txn.paymentStatus || "UNKNOWN").toUpperCase();
                  // --- SAFETY CHECKS END ---

                  return (
                    <tr key={t.id || Math.random()}>
                      <td data-label="Name">{fullName}</td>
                      <td data-label="Customer ID">{displayUserId}</td>
                      <td data-label="Transaction ID">
                        {txn.transactionReference || "N/A"}
                      </td>
                      <td data-label="FX Type">{`${t.fromCurrency} → ${t.toCurrency}`}</td>
                      <td data-label="Rate">{t.currentRate}</td>
                      <td data-label="Time Stamp">
                        {t.createdAt
                          ? new Date(t.createdAt).toLocaleString()
                          : "N/A"}
                      </td>
                      <td data-label="Amount">{amount}</td>
                      <td data-label="Status">
                        <span
                          className={`status-badge status-${status.toLowerCase()}`}
                        >
                          {status}
                        </span>
                      </td>
                      <td data-label="Action" className="action-cell">
                        <ComingSoonAction title="Transaction details — coming soon">
                          <button
                            type="button"
                            className="table-action-btn"
                            aria-label="View transaction"
                            disabled
                          >
                            <IoEyeOutline />
                          </button>
                        </ComingSoonAction>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setCurrentPage} loading={loading} />
        </>
      )}
    </div>
  );
};
export default FXPage;
